import { useEffect, useMemo, useState } from 'react';

import useTeams from '../../../../hooks/api/teams/useTeams';
import { selectOptionFactory } from '../../../../utils';
import type { Team } from '../../../../../shared/model-typings';
import type { SelectOption } from '../../../../types';

export type TeamSelectOption = SelectOption<number>;

const teamToSelectOption = (team: Team): TeamSelectOption => selectOptionFactory(team.id, team.name);

type UseTeamsSelectOptionsProps = {
  selectedIds?: number[],
  onTeamsChange?: (teamIds: number[]) => void,
};

export type UseTeamsSelectOptionsReturn = {
  isLoading: boolean;
  error: Error | null;
  teamOptions: TeamSelectOption[];
  selectedTeamOptions: TeamSelectOption[];
  onTeamsSelected: (selectedTeams: TeamSelectOption[]) => void;
};

/**
 * Wrapper around useTeams hook to provide select options for teams.
 * Only exposes the teams as SelectOptions.
 *
 * @param {UseTeamsSelectOptionsProps} props - Props for the hook.
 * @param {number[]} [props.selectedIds] - Pre-selected team IDs. This is useful if hydrating this from the URL params.
 * @param {(teamIds: number[]) => void} [props.onTeamsChange] - Callback to be called when teams are selected. This allows you to update some upper level state.
 */
const useTeamsSelectOptions = ({
  selectedIds,
  onTeamsChange,
}: UseTeamsSelectOptionsProps = {}): UseTeamsSelectOptionsReturn => {
  const { data: teams, isLoading, error } = useTeams();

  //  We only need to store the IDs of which teams are selected, and can derive the Team SelectOptions from the IDs
  const [selectedTeamsIds, setSelectedTeamsIds] = useState<Set<number>>(new Set(selectedIds));

  //  This ensures that if the parent is telling us that there are pre-selected teams, we update our state to reflect that.
  useEffect(() => {
    if (selectedIds) {
      setSelectedTeamsIds(new Set(selectedIds));
    }
  }, [selectedIds]);

  //  All available teams as SelectOptions
  const teamOptions: TeamSelectOption[] = useMemo(() => {
    return teams?.map(teamToSelectOption) ?? [];
  }, [teams]);

  //  All selected teams as SelectOptions
  const selectedTeamOptions: TeamSelectOption[] = useMemo(() => {
    return teams
      ?.filter((team) => selectedTeamsIds.has(team.id))
      .map(teamToSelectOption) ?? [];
  }, [teams, selectedTeamsIds]);

  const onTeamsSelected = (selectedTeams: Array<SelectOption<number>>) => {
    const selectedTeamIds = new Set(selectedTeams.map((team) => Number(team.value)));
    setSelectedTeamsIds(selectedTeamIds);
    onTeamsChange?.(Array.from(selectedTeamIds));
  };

  return {
    isLoading,
    error,
    teamOptions,
    selectedTeamOptions,
    onTeamsSelected,
  };
};

export default useTeamsSelectOptions;
